/**
 * Opens Shopify Inbox using available component methods and provider APIs,
 * with shadow DOM and legacy iframe fallbacks. These integrations depend on
 * third-party behavior and may need updates when the widget changes.
 */
(function() {
  const CHAT_NAME_HINTS = ['chat', 'inbox', 'message'];
  const MAX_SHADOW_DEPTH = 12;
  const CHAT_TRIGGER_SELECTOR = [
    '#chat-link',
    '[data-open-chat]',
    '[data-action="open-chat"]',
    'a[href="#chat"]',
    'a[href="#live-chat"]',
    'a[href="#shopify-chat"]',
    '[data-chat-link]'
  ].join(', ');

  function isVisible(el) {
    if (!el) return false;
    return el.getClientRects().length > 0;
  }

  function isLikelyChatToggle(button) {
    if (!button) return false;
    const label = (
      (button.getAttribute('aria-label') || '') + ' ' +
      (button.getAttribute('aria-controls') || '') + ' ' +
      (button.getAttribute('part') || '') + ' ' +
      (button.className || '') + ' ' +
      (button.textContent || '')
    ).toLowerCase();
    return CHAT_NAME_HINTS.some((hint) => label.includes(hint));
  }

  /**
   * Recursively collect all shadow roots reachable from `root` up to MAX_SHADOW_DEPTH.
   */
  function collectShadowRoots(root, depth, out) {
    if (!root || depth > MAX_SHADOW_DEPTH) return out;
    const elements = root.querySelectorAll('*');
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i];
      if (el.shadowRoot) {
        out.push(el.shadowRoot);
        collectShadowRoots(el.shadowRoot, depth + 1, out);
      }
    }
    return out;
  }

  /**
   * Dispatches a complete pointer/mouse gesture sequence so custom elements
   * listening to pointerdown, mousedown, pointerup, mouseup, or click respond.
   */
  function simulateClick(el) {
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const eventInit = {
      bubbles: true,
      cancelable: true,
      composed: true,
      view: window,
      clientX: rect.left + rect.width / 2,
      clientY: rect.top + rect.height / 2
    };

    ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click'].forEach((type) => {
      const EventCtor = type.startsWith('pointer') && window.PointerEvent ? PointerEvent : MouseEvent;
      try {
        el.dispatchEvent(new EventCtor(type, eventInit));
      } catch (e) {
        // Fallback for environments lacking specific event constructors
        if (type === 'click' && typeof el.click === 'function') {
          el.click();
        }
      }
    });
  }

  /**
   * Attempt to open chat via native custom element methods and properties.
   */
  function tryComponentAPI() {
    // 1. Check <shopify-chat>
    const sc = document.querySelector('shopify-chat, [view="chat"]');
    if (sc) {
      if (sc.open === true) return true; // Already open

      if (typeof sc.show === 'function') {
        try {
          sc.show();
          return true;
        } catch (e) {}
      }
    }

    // 2. Check <shopify-agent-runtime> in document or inside shopify-chat's shadowRoot
    const agent = sc?.shadowRoot?.querySelector('shopify-agent-runtime') ||
      document.querySelector('shopify-agent-runtime');

    if (agent) {
      if (agent.open === true) return true; // Already open

      if (typeof agent.show === 'function') {
        try {
          agent.show();
          return true;
        } catch (e) {}
      }
    }

    return false;
  }

  /**
   * Attempt to open chat via third-party / global SDK APIs.
   */
  function tryGlobalAPIs() {
    // Shopify Inbox / Shopify Chat globals
    if (window.ShopifyChat && typeof window.ShopifyChat.open === 'function') {
      try { window.ShopifyChat.open(); return true; } catch (e) {}
    }
    if (window.ShopifyChat && typeof window.ShopifyChat.show === 'function') {
      try { window.ShopifyChat.show(); return true; } catch (e) {}
    }
    if (window.Shopify?.Inbox && typeof window.Shopify.Inbox.open === 'function') {
      try { window.Shopify.Inbox.open(); return true; } catch (e) {}
    }

    // Gorgias Chat
    if (window.GorgiasChat && typeof window.GorgiasChat.open === 'function') {
      try { window.GorgiasChat.open(); return true; } catch (e) {}
    }

    // Tidio Chat
    if (window.tidioChatApi && typeof window.tidioChatApi.open === 'function') {
      try { window.tidioChatApi.open(); return true; } catch (e) {}
    }

    // Zendesk Web Widget
    if (typeof window.zE === 'function') {
      try { window.zE('messenger', 'open'); return true; } catch (e) {}
      try { window.zE('webWidget', 'open'); return true; } catch (e) {}
    }

    // Crisp Chat
    if (Array.isArray(window.$crisp)) {
      try { window.$crisp.push(['do', 'chat:open']); return true; } catch (e) {}
    }

    // Intercom
    if (typeof window.Intercom === 'function') {
      try { window.Intercom('show'); return true; } catch (e) {}
    }

    return false;
  }

  /**
   * Search all shadow roots for an activator or toggle button.
   */
  function locateToggleInShadow() {
    const shadowRoots = collectShadowRoots(document, 0, []);

    // 1. Look for part="activator"
    for (const root of shadowRoots) {
      const match = Array.from(root.querySelectorAll('[part~="activator"]')).find(isVisible);
      if (match) {
        if (match.getAttribute('aria-expanded') === 'true') return { el: match, alreadyOpen: true };
        return { el: match, alreadyOpen: false };
      }
    }

    // 2. Look for aria-expanded button matching chat heuristics
    for (const root of shadowRoots) {
      const buttons = Array.from(root.querySelectorAll('button')).filter(isVisible);
      const expMatch = buttons.find((btn) => btn.hasAttribute('aria-expanded') && isLikelyChatToggle(btn));
      if (expMatch) {
        if (expMatch.getAttribute('aria-expanded') === 'true') return { el: expMatch, alreadyOpen: true };
        return { el: expMatch, alreadyOpen: false };
      }
    }

    // 3. Look for any visible button matching chat hints
    for (const root of shadowRoots) {
      const buttons = Array.from(root.querySelectorAll('button')).filter(isVisible);
      const chatMatch = buttons.find(isLikelyChatToggle);
      if (chatMatch) {
        return { el: chatMatch, alreadyOpen: false };
      }
    }

    // 4. An unlabeled sole button is safe to consider only inside Shopify chat.
    // Follow host ancestors so nested runtime shadow roots are included.
    for (const root of shadowRoots) {
      let ancestor = root;
      while (ancestor && ancestor.host) {
        if (ancestor.host.matches('shopify-chat, shopify-agent-runtime, #ShopifyChat')) {
          const buttons = Array.from(root.querySelectorAll('button')).filter(isVisible);
          if (buttons.length === 1) {
            const button = buttons[0];
            return { el: button, alreadyOpen: button.getAttribute('aria-expanded') === 'true' };
          }
          break;
        }
        ancestor = ancestor.host.getRootNode();
      }
    }

    return null;
  }

  /**
   * Search light DOM and iframes for chat launchers.
   */
  function locateToggleInLightDOM() {
    // 1. Iframe launcher
    const iframes = Array.from(document.querySelectorAll(
      '#dummy-chat-button-iframe, iframe[id*="chat" i], iframe[src*="shopify-chat" i], iframe[title*="chat" i]'
    ));

    for (const iframe of iframes) {
      try {
        const doc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document);
        if (doc) {
          const button = doc.querySelector('button, .chat-toggle, [part~="activator"]');
          if (button) return { el: button, alreadyOpen: button.getAttribute('aria-expanded') === 'true' };
        }
      } catch (e) {
        // Cross-origin iframe
      }
    }

    // 2. Standard light DOM chat toggle
    const lightBtn = document.querySelector(
      '#ShopifyChat button, [id*="shopify-chat"] button, [class*="shopify-chat"] button'
    );
    if (lightBtn) return { el: lightBtn, alreadyOpen: lightBtn.getAttribute('aria-expanded') === 'true' };

    return null;
  }

  /**
   * Tries every strategy to ensure the chat window is opened.
   * Returns true if opened or already open; false otherwise.
   */
  function tryOpenChat() {
    // Strategy 1: Component methods (fastest, most resilient to styling changes)
    if (tryComponentAPI()) {
      return true;
    }

    // Strategy 2: Global SDK APIs
    if (tryGlobalAPIs()) {
      return true;
    }

    // Strategy 3: Shadow DOM search
    const shadowMatch = locateToggleInShadow();
    if (shadowMatch) {
      if (shadowMatch.alreadyOpen) {
        return true; // Already open, prevent toggling shut
      }
      simulateClick(shadowMatch.el);
      return true;
    }

    // Strategy 4: Light DOM & Iframes
    const lightMatch = locateToggleInLightDOM();
    if (lightMatch) {
      if (lightMatch.alreadyOpen) return true;
      simulateClick(lightMatch.el);
      return true;
    }

    return false;
  }

  /**
   * Opens the chat widget. If the widget is still loading or mounting,
   * attaches observers and lifecycle hooks to open it the moment it appears.
   */
  window.openShopifyChat = function openShopifyChat() {
    if (tryOpenChat()) return;

    let cleanup = null;
    let stopped = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 50; // 50 * 200ms = 10s
    const INTERVAL_MS = 200;

    const stop = () => {
      stopped = true;
      if (cleanup) {
        cleanup();
        cleanup = null;
      }
    };

    const check = () => {
      if (stopped) return;
      if (tryOpenChat() || attempts >= MAX_ATTEMPTS) {
        stop();
      }
    };

    // 1. Polling interval
    const intervalId = setInterval(() => {
      attempts += 1;
      check();
    }, INTERVAL_MS);

    // 2. Custom Element defined hooks
    if (window.customElements && typeof window.customElements.whenDefined === 'function') {
      window.customElements.whenDefined('shopify-chat').then(check).catch(() => {});
      window.customElements.whenDefined('shopify-agent-runtime').then(check).catch(() => {});
    }

    // 3. MutationObserver for instant trigger on element insertion
    let observer = null;
    if (typeof MutationObserver !== 'undefined' && document.documentElement) {
      observer = new MutationObserver(() => {
        check();
      });
      observer.observe(document.documentElement, {
        childList: true,
        subtree: true
      });
    }

    cleanup = () => {
      clearInterval(intervalId);
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    };
  };

  // Delegated click listener across the document
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest(CHAT_TRIGGER_SELECTOR);
    if (!trigger) return;

    event.preventDefault();
    window.openShopifyChat();
  });

  // Keyboard accessibility (Enter and Space) on interactive trigger elements
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const trigger = event.target.closest(CHAT_TRIGGER_SELECTOR);
    if (!trigger) return;

    event.preventDefault();
    window.openShopifyChat();
  });
})();
